import { JobsActions, JobsActionTypes } from '../actions/jobs.actions';
import { ListingActions, ListingActionTypes } from '../actions/listing.actions';
import { JobListing } from '../models/listing.model';
import { Job } from '../models/job.model';
import { Assignment } from '../models/assignment.model';

export interface SelectedJobState {
  currentStep: string;
  isValid: boolean;
  job: Job;
  error: any;
  listing: JobListing;
  lastUpdated: Date;
  isSaved: boolean;
  shouldReloadAssignments: boolean;
}

export interface State {
  jobs: Array<Job>;
  selectedJobState: SelectedJobState;
  categories: any[];
  jobTitleSuggestions: any[];
  jobAssignments: Assignment[];
}

const initialState: State = {
  jobs: [],
  selectedJobState: {
    currentStep: 'info',
    isValid: false,
    job: null,
    error: null,
    listing: null,
    lastUpdated: null,
    isSaved: true,
    shouldReloadAssignments: false,
  },
  categories: [],
  jobTitleSuggestions: [],
  jobAssignments: [],
};

export function jobsReducer(
  state = initialState,
  action: JobsActions | ListingActions
) {
  switch (action.type) {
    case JobsActionTypes.LoadJobsSuccess:
      return {
        ...state,
        jobs: action.payload.jobs,
        selectedJobState: initialState.selectedJobState,
      };

    case JobsActionTypes.ChangeJobValidationState:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          isValid: action.payload.isValid,
        },
      };

    case JobsActionTypes.LoadJob:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          job: null,
          error: null,
          listing: null,
          lastUpdated: null,
          isSaved: true,
        },
      };

    case JobsActionTypes.LoadJobSuccess:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          job: action.payload.job,
        },
      };

    case JobsActionTypes.UnpublishJobSuccess:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          job: action.payload.job,
        },
      };

    case JobsActionTypes.LoadJobCategoriesSuccess:
      return { ...state, categories: action.payload.categories };

    case JobsActionTypes.LoadJobTitleSuggestionsSuccess:
      return {
        ...state,
        jobTitleSuggestions: action.payload.jobTitleSuggestions,
      };

    case JobsActionTypes.RequestToLoadJobAssignments:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          shouldReloadAssignments: true,
        },
        jobAssignments: [],
      };
    case JobsActionTypes.LoadJobAssignmentsSuccess:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          shouldReloadAssignments: false,
        },
        jobAssignments: action.payload.jobAssignments,
      };

    case JobsActionTypes.SaveJob:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          job: action.payload.job,
          listing: action.payload.listing
            ? action.payload.listing
            : state.selectedJobState.listing,
        },
      };

    case JobsActionTypes.SaveJobSuccess:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          job: {
            ...action.payload.job,
          },
          isSaved: true,
          lastUpdated: action.payload.lastUpdated,
        },
      };

    case ListingActionTypes.SaveListing:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          listing: {
            ...state.selectedJobState.listing,
            ...action.payload.listing,
          },
        },
      };

    case ListingActionTypes.UseListing:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          listing: action.payload.listing,
        },
      };

    case JobsActionTypes.JobActionFail:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          error: action.payload.error,
        },
      };

    case ListingActionTypes.LoadListingSuccess:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          listing: action.payload.listing,
        },
      };

    case JobsActionTypes.SetOneWayVideoInterview:
      return {
        ...state,
        selectedJobState: {
          ...state.selectedJobState,
          job: {
            ...state.selectedJobState.job,
            has_video_interview: action.payload.hasVideoInterview,
          },
        },
      };

    default:
      return state;
  }
}

export const getJobs = (state: State) => state.jobs;
export const getCategories = (state: State) => state.categories;
export const getJobTitleSuggestions = (state: State) =>
  state.jobTitleSuggestions;
export const getJobAssignments = (state: State) => state.jobAssignments;
export const getSelectedJobState = (state: State) => state.selectedJobState;
export const isJobValid = (state: State) => state.selectedJobState.isValid;
